<template>
  <WelfareShell active="welfare-settings" subtitle="เกณฑ์ที่ใช้วิเคราะห์ การจับคู่สวัสดิการที่ครัวเรือนแจ้งว่าได้รับ สำรอง/นำเข้าข้อมูล และประวัติการแก้ไข">
    <template #actions>
      <button type="button" class="btn btn-outline-secondary" :disabled="loading" @click="load"><RefreshCwIcon class="w-4 h-4 mr-1" /> โหลดใหม่</button>
    </template>

    <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <!-- poverty line -->
      <div class="box p-4">
        <div class="flex items-center gap-2 mb-3">
          <SlidersIcon class="w-4 h-4" style="color:#415A77" />
          <div class="text-sm font-semibold text-gray-800">เกณฑ์เส้นความยากจน</div>
        </div>
        <div class="text-sm text-gray-700 mb-3">รายได้เฉลี่ยต่อคนต่อปีที่ต่ำกว่าเกณฑ์นี้จะถือเป็น "รายได้ครัวเรือนต่ำกว่าเส้นความยากจน" (ใช้เกณฑ์บัตรสวัสดิการแห่งรัฐ) และใช้กับกฎที่อ้างค่าคำนวณ <span class="font-mono text-xs">hh.income_per_capita_year</span></div>
        <div class="flex flex-wrap items-end gap-3">
          <div>
            <label class="form-label">บาท / คน / ปี</label>
            <input v-model="metaForm.income_per_capita_year_max" type="number" min="1" step="1000" class="form-control w-48">
          </div>
          <div class="text-sm text-theme-37 pb-2">≈ {{ perMonth }} บาท/คน/เดือน</div>
          <button type="button" class="btn btn-primary bg-primary-btn border-primary-btn" :disabled="savingMeta" @click="saveMeta"><SaveIcon class="w-4 h-4 mr-1" /> บันทึก</button>
        </div>
        <div v-if="meta" class="text-xs text-theme-37 mt-3">เวอร์ชันเครื่องมือวิเคราะห์ {{ meta.engine_version }} <span v-if="meta.updated_at">· แก้ไขล่าสุด {{ fmtDate(meta.updated_at) }}<span v-if="meta.updated_by && meta.updated_by.username"> โดย {{ meta.updated_by.username }}</span></span></div>
      </div>

      <!-- backup / import / cache -->
      <div class="box p-4">
        <div class="flex items-center gap-2 mb-3">
          <DatabaseIcon class="w-4 h-4" style="color:#415A77" />
          <div class="text-sm font-semibold text-gray-800">สำรองและนำเข้าข้อมูลการตั้งค่า</div>
        </div>
        <div class="text-sm text-gray-700 mb-3">ดาวน์โหลดรายการสวัสดิการ กฎ และการจับคู่ทั้งหมดเป็นไฟล์เดียว เพื่อเก็บเป็นเวอร์ชันหรือนำไปใช้กับระบบอื่น (เช่น ย้ายจากระบบทดสอบไประบบจริง)</div>
        <div class="flex flex-wrap gap-2">
          <button type="button" class="btn btn-outline-secondary" :disabled="exporting" @click="doExport"><DownloadIcon class="w-4 h-4 mr-1" /> ดาวน์โหลดไฟล์สำรอง (JSON)</button>
          <label class="btn btn-outline-secondary cursor-pointer"><UploadIcon class="w-4 h-4 mr-1" /> นำเข้าจากไฟล์<input type="file" accept="application/json,.json" class="hidden" @change="pickImport"></label>
          <button type="button" class="btn btn-outline-secondary" @click="refreshCache"><ZapIcon class="w-4 h-4 mr-1" /> ให้หน้าครัวเรือนใช้ค่าล่าสุดทันที</button>
        </div>
        <div v-if="importPreview" class="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
          <div class="font-medium text-gray-800 mb-1">ตรวจสอบไฟล์ก่อนนำเข้า</div>
          <div class="text-theme-37 text-xs mb-2">ส่งออกเมื่อ {{ importPreview.exported_at ? fmtDate(importPreview.exported_at) : '-' }}</div>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <div>สวัสดิการ <span class="font-medium">{{ (importPreview.catalog || []).length }}</span></div>
            <div>กฎ <span class="font-medium">{{ (importPreview.rules || []).length }}</span></div>
            <div>การอ่านคำตอบ <span class="font-medium">{{ (importPreview.fieldmap || []).length }}</span></div>
            <div>รหัสรับสวัสดิการ <span class="font-medium">{{ (importPreview.receipt_map || []).length }}</span></div>
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <label class="flex items-center gap-1"><input v-model="importMode" type="radio" class="form-check-input" value="upsert"> เพิ่ม/อัปเดตทับรายการที่มีอยู่</label>
            <label class="flex items-center gap-1"><input v-model="importMode" type="radio" class="form-check-input" value="replace"> <span class="text-theme-24">ล้างข้อมูลเดิมทั้งหมดแล้วแทนที่</span></label>
            <button type="button" class="btn btn-primary bg-primary-btn border-primary-btn ml-auto" :disabled="importing" @click="doImport">นำเข้า</button>
            <button type="button" class="btn btn-outline-secondary" @click="importPreview = null">ยกเลิก</button>
          </div>
        </div>
        <div class="text-xs text-theme-37 mt-3">หมายเหตุ: การแก้ไขทุกอย่างมีผลกับหน้าครัวเรือนทันทีในเครื่องแม่ข่ายที่รับคำสั่ง หากมีหลายเครื่องจะทยอยอัปเดตภายใน 5 นาที</div>
      </div>

      <!-- receipt map -->
      <div class="box p-4 xl:col-span-2">
        <div class="flex items-center gap-2 mb-1">
          <CheckCircleIcon class="w-4 h-4" style="color:#42B983" />
          <div class="text-sm font-semibold text-gray-800">สวัสดิการที่ครัวเรือนแจ้งว่า "ได้รับแล้ว"</div>
        </div>
        <div class="text-sm text-gray-700 mb-3">จับคู่ตัวเลือกในคำถามสวัสดิการที่ได้รับ (WELFARE) ของแบบสอบถามครัวเรือน กับสวัสดิการในระบบ เพื่อให้หน้าครัวเรือนแยกได้ว่าอะไรได้รับแล้ว และตรวจจับกรณีที่ควรได้แต่ยังไม่ได้รับ</div>
        <div class="overflow-x-auto">
          <table class="table table-admin w-full">
            <thead><tr><th class="w-24">รหัสตัวเลือก</th><th>ชื่อในแบบสอบถาม</th><th class="min-w-[20rem]">สวัสดิการในระบบ</th><th class="w-28 text-center">สถานะ</th></tr></thead>
            <tbody>
              <tr v-for="r in receipt" :key="r.welfare_code">
                <td class="text-sm text-theme-37">{{ r.welfare_code }}</td>
                <td class="text-sm text-gray-800">{{ r.name }}</td>
                <td>
                  <select class="form-select" :value="r.welfare_id || ''" @change="saveReceipt(r, $event.target.value)">
                    <option value="">— ยังไม่จับคู่ —</option>
                    <option v-for="c in catalog" :key="c.welfare_id" :value="c.welfare_id">{{ c.welfare_id }} · {{ c.name }}</option>
                  </select>
                </td>
                <td class="text-center"><span class="chip" :style="r.welfare_id ? { color: '#1f6b45', background: '#e6f5ec' } : SEVERITY_STYLE.monitor">{{ r.welfare_id ? 'จับคู่แล้ว' : 'ยังไม่จับคู่' }}</span></td>
              </tr>
            </tbody>
          </table>
          <div v-if="!receipt.length && !loading" class="empty-state">ไม่พบตัวเลือกสวัสดิการในแบบสอบถาม</div>
        </div>
      </div>

      <!-- audit -->
      <div class="box p-4 xl:col-span-2">
        <div class="flex flex-wrap items-center gap-2 mb-3">
          <ClockIcon class="w-4 h-4" style="color:#415A77" />
          <div class="text-sm font-semibold text-gray-800">ประวัติการแก้ไข</div>
          <select v-model="auditCol" class="form-select w-52 ml-auto" @change="loadAudit">
            <option value="">ทุกส่วน</option>
            <option value="MPI_LOGBOOK_WELFARE_CATALOG">รายการสวัสดิการ</option>
            <option value="MPI_LOGBOOK_WELFARE_RULE">กฎและการจับคู่</option>
            <option value="MPI_LOGBOOK_WELFARE_FIELDMAP">การอ่านคำตอบ</option>
            <option value="MPI_LOGBOOK_WELFARE_RECEIPT_MAP">รหัสรับสวัสดิการ</option>
            <option value="MPI_LOGBOOK_WELFARE_META">เกณฑ์</option>
          </select>
        </div>
        <div class="overflow-x-auto">
          <table class="table table-admin w-full">
            <thead><tr><th class="w-40">เวลา</th><th class="w-36">ผู้แก้ไข</th><th class="w-44">การกระทำ</th><th>รายการ</th><th class="w-16"></th></tr></thead>
            <tbody>
              <tr v-for="a in audit" :key="a._id">
                <td class="text-sm text-theme-37 whitespace-nowrap">{{ fmtDate(a.at) }}</td>
                <td class="text-sm">{{ (a.by && (a.by.username || a.by.email || a.by.name)) || '-' }}</td>
                <td class="text-sm"><span class="chip chip-muted">{{ ACTION_TH[a.action] || a.action }}</span></td>
                <td class="text-sm text-gray-800 break-words">{{ auditSubject(a) }}</td>
                <td class="text-center"><button type="button" class="icon-btn icon-btn-plain" title="ดูรายละเอียด" @click="auditDetail = a"><EyeIcon class="w-4 h-4" /></button></td>
              </tr>
            </tbody>
          </table>
          <div v-if="!audit.length" class="empty-state">ยังไม่มีประวัติการแก้ไข</div>
        </div>
      </div>
    </div>

    <WModal :show="!!auditDetail" title="รายละเอียดการแก้ไข" :subtitle="auditDetail ? `${ACTION_TH[auditDetail.action] || auditDetail.action} · ${fmtDate(auditDetail.at)}` : ''" size="lg" @close="auditDetail = null">
      <div v-if="auditDetail" class="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <div><div class="font-medium text-gray-800 mb-1">ก่อนแก้ไข</div><pre class="rounded-md bg-gray-50 border border-gray-200 p-2 overflow-auto max-h-96 whitespace-pre-wrap break-words">{{ pretty(auditDetail.before) }}</pre></div>
        <div><div class="font-medium text-gray-800 mb-1">หลังแก้ไข</div><pre class="rounded-md bg-gray-50 border border-gray-200 p-2 overflow-auto max-h-96 whitespace-pre-wrap break-words">{{ pretty(auditDetail.after) }}</pre></div>
      </div>
    </WModal>
  </WelfareShell>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import WelfareShell from "../components/WelfareShell.vue";
import WModal from "../components/WModal.vue";
import { welfareApi, apiErrorMessage } from "../api";
import { SEVERITY_STYLE } from "../constants";
import { toastSuccess, toastError, confirmDialog, alertError } from "../components/useToast";

const ACTION_TH = {
  "catalog.create": "เพิ่มสวัสดิการ", "catalog.update": "แก้ไขสวัสดิการ", "catalog.delete": "ลบสวัสดิการ", "catalog.reorder": "จัดลำดับสวัสดิการ",
  "rule.create": "เพิ่มกฎ", "rule.update": "แก้ไขกฎ", "rule.patch": "ปรับกฎ", "rule.delete": "ลบกฎ", "rule.reorder": "จัดลำดับกฎ",
  "rule.set_welfare": "จับคู่ปัญหา–สวัสดิการ", "rule.unlink_welfare": "ถอดสวัสดิการออกจากกฎ",
  "fieldmap.auto_create": "เพิ่มการอ่านคำตอบอัตโนมัติ", "fieldmap.update": "แก้ไขการอ่านคำตอบ", "fieldmap.create": "เพิ่มการอ่านคำตอบ",
  "receipt.update": "จับคู่รหัสรับสวัสดิการ", "receipt.create": "เพิ่มรหัสรับสวัสดิการ", "meta.update": "แก้ไขเกณฑ์",
  "bundle.import.upsert": "นำเข้าไฟล์ (อัปเดต)", "bundle.import.replace": "นำเข้าไฟล์ (แทนที่)", "cache.refresh": "ล้างแคช",
};

const loading = ref(false);
const meta = ref(null);
const metaForm = reactive({ income_per_capita_year_max: 100000 });
const savingMeta = ref(false);
const receipt = ref([]);
const catalog = ref([]);
const audit = ref([]);
const auditCol = ref("");
const auditDetail = ref(null);
const exporting = ref(false);
const importing = ref(false);
const importPreview = ref(null);
const importMode = ref("upsert");

const perMonth = computed(() => Math.round((Number(metaForm.income_per_capita_year_max) || 0) / 12).toLocaleString("th-TH"));
const fmtDate = (d) => (d ? new Date(d).toLocaleString("th-TH", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : "-");
const pretty = (v) => (v == null ? "—" : JSON.stringify(v, null, 2));
const auditSubject = (a) => {
  const doc = a.after || a.before || {};
  const name = doc.name_th || doc.name || (a.after && a.after.counts ? Object.entries(a.after.counts).map(([k, v]) => `${k.replace("MPI_LOGBOOK_WELFARE_", "")}: ${v}`).join(", ") : "");
  return [a.doc_id, name].filter(Boolean).join(" · ") || (a.after && a.after.ids ? `${a.after.ids.length} รายการ` : "-");
};

async function load() {
  loading.value = true;
  try {
    const [m, r, c] = await Promise.all([welfareApi.meta.get(), welfareApi.receipt.list(), welfareApi.catalog.list()]);
    meta.value = m;
    metaForm.income_per_capita_year_max = m.income_per_capita_year_max || 100000;
    receipt.value = r.items || [];
    catalog.value = c.items || [];
    await loadAudit();
  } catch (e) {
    toastError(apiErrorMessage(e, "โหลดข้อมูลไม่สำเร็จ"));
  } finally {
    loading.value = false;
  }
}
async function loadAudit() {
  try {
    const a = await welfareApi.audit({ limit: 100, ...(auditCol.value ? { collection: auditCol.value } : {}) });
    audit.value = a.items || [];
  } catch (e) { toastError(apiErrorMessage(e, "โหลดประวัติไม่สำเร็จ")); }
}
async function saveMeta() {
  savingMeta.value = true;
  try {
    const res = await welfareApi.meta.update({ income_per_capita_year_max: Number(metaForm.income_per_capita_year_max) });
    meta.value = res.item;
    toastSuccess("บันทึกเกณฑ์แล้ว");
    loadAudit();
  } catch (e) { alertError(apiErrorMessage(e)); } finally { savingMeta.value = false; }
}
async function saveReceipt(r, welfareId) {
  try {
    const res = await welfareApi.receipt.update(r.welfare_code, { welfare_id: welfareId || null, name: r.name });
    r.welfare_id = res.item.welfare_id;
    toastSuccess("บันทึกการจับคู่แล้ว");
    loadAudit();
  } catch (e) { alertError(apiErrorMessage(e)); }
}
async function doExport() {
  exporting.value = true;
  try {
    const bundle = await welfareApi.exportBundle();
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `welfare-config-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  } catch (e) { alertError(apiErrorMessage(e)); } finally { exporting.value = false; }
}
function pickImport(evt) {
  const file = evt.target.files && evt.target.files[0];
  evt.target.value = "";
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => {
    try {
      const json = JSON.parse(reader.result);
      if (json.format !== "mpi-logbook-welfare-bundle") throw new Error("ไม่ใช่ไฟล์สำรองของระบบนี้");
      importPreview.value = json;
      importMode.value = "upsert";
    } catch (e) { alertError(e.message || "อ่านไฟล์ไม่สำเร็จ"); }
  };
  reader.readAsText(file);
}
async function doImport() {
  const ok = await confirmDialog({
    title: importMode.value === "replace" ? "ล้างข้อมูลเดิมและแทนที่ทั้งหมด?" : "นำเข้าและอัปเดตทับรายการที่มีอยู่?",
    text: importMode.value === "replace" ? "ข้อมูลสวัสดิการ กฎ และการจับคู่ปัจจุบันจะถูกลบทั้งหมดก่อนนำเข้า" : "รายการที่มีรหัสเดียวกันจะถูกแทนที่ด้วยข้อมูลจากไฟล์",
    confirmText: "นำเข้า",
  });
  if (!ok) return;
  importing.value = true;
  try {
    const res = await welfareApi.importBundle(importPreview.value, importMode.value);
    toastSuccess(`นำเข้าแล้ว: ${Object.values(res.counts || {}).reduce((a, b) => a + b, 0)} รายการ`);
    importPreview.value = null;
    await load();
  } catch (e) { alertError(apiErrorMessage(e)); } finally { importing.value = false; }
}
async function refreshCache() {
  try { await welfareApi.refreshCache(); toastSuccess("ล้างแคชแล้ว หน้าครัวเรือนจะใช้ค่าล่าสุดทันที"); loadAudit(); } catch (e) { toastError(apiErrorMessage(e)); }
}
onMounted(load);
</script>
